import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldOff, KeyRound } from "lucide-react";
import api, { extractError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function TwoFactorSection() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null); // {secret, qr_png_base64}
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("دو مرحلی توثیق فعال ہو گئی");
      setSetup(null); setCode("");
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: pw, code });
      toast.success("دو مرحلی توثیق بند کر دی گئی");
      setPw(""); setCode("");
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="twofa-section">
      <h2 className="urdu-naskh text-xl font-semibold text-primary flex items-center gap-2">
        {user?.totp_enabled ? <ShieldCheck size={18} className="text-emerald-600" /> : <ShieldOff size={18} />}
        دو مرحلی توثیق (2FA)
      </h2>
      {user?.totp_enabled ? (
        <div className="space-y-3">
          <p className="urdu-naskh text-sm text-emerald-700">آپ کا اکاؤنٹ دو مرحلی توثیق سے محفوظ ہے۔</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="urdu-naskh">پاس ورڈ</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} dir="ltr" data-testid="twofa-disable-pw" />
            </div>
            <div>
              <Label className="urdu-naskh">موجودہ کوڈ</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" dir="ltr" data-testid="twofa-disable-code" />
            </div>
          </div>
          <Button onClick={disable} disabled={busy} variant="destructive" data-testid="twofa-disable">
            2FA بند کریں
          </Button>
        </div>
      ) : setup ? (
        <div className="space-y-3">
          <p className="urdu-naskh text-sm">
            گوگل آتھینٹیکیٹر / Authy میں یہ QR اسکین کریں، پھر ٦ ہندسوں کا کوڈ نیچے درج کریں۔
          </p>
          <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="2FA QR" className="w-48 h-48 border border-border rounded p-1 mx-auto sm:mx-0" />
          <div className="text-xs font-eng break-all bg-muted p-2 rounded" dir="ltr">
            <b>Secret:</b> {setup.secret}
          </div>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" dir="ltr" className="max-w-40" data-testid="twofa-verify-code" />
            <Button onClick={verify} disabled={busy || !code} className="bg-primary" data-testid="twofa-verify">
              تصدیق کریں
            </Button>
            <Button variant="outline" onClick={() => setSetup(null)}>منسوخ</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="urdu-naskh text-sm text-muted-foreground">
            پاس ورڈ کے علاوہ لاگ اِن کے وقت آپ کی موبائل ایپ سے ایک اضافی کوڈ درکار ہوگا۔
          </p>
          <Button onClick={startSetup} disabled={busy} className="bg-primary gap-2" data-testid="twofa-start">
            <KeyRound size={16} /> 2FA سیٹ اپ کریں
          </Button>
        </div>
      )}
    </Card>
  );
}
